import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";

import { ApiError, auth } from "@/lib/api.ts";
import type { SessionUser } from "@/lib/api-types.ts";

interface SessionValue {
  user: SessionUser | null;
  isLoading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInError: string | null;
  isSigningIn: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ["session"],
    queryFn: auth.me,
    // A 401 here is the normal "not signed in" answer, not a failure to retry.
    retry: false,
    staleTime: 60_000,
  });

  const signIn = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      auth.login(username, password),
    onSuccess: (user) => queryClient.setQueryData(["session"], user),
  });

  const signOut = useMutation({
    mutationFn: auth.logout,
    onSuccess: () => {
      queryClient.setQueryData(["session"], null);
      // Conversations, collections and the corpus are all per-caller. Keeping any of
      // it after a sign-out would show the next user the previous one's data.
      queryClient.clear();
    },
  });

  const value: SessionValue = {
    user: me.data ?? null,
    isLoading: me.isLoading,
    signIn: async (username, password) => {
      await signIn.mutateAsync({ username, password });
    },
    signOut: async () => {
      await signOut.mutateAsync();
    },
    signInError: signIn.error instanceof ApiError ? signIn.error.message : null,
    isSigningIn: signIn.isPending,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}

/** The signed-in user, or a throw. For screens that a guard has already gated. */
export function useUser(): SessionUser {
  const { user } = useSession();
  if (!user) throw new Error("useUser called outside an authenticated route");
  return user;
}
